import {
  AmbientLight,
  Box3,
  type BufferGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  MeshPhysicalMaterial,
  MultiplyOperation,
  type Object3D,
  PerspectiveCamera,
  Scene as ThreeScene,
  Vector2,
  WebGLRenderer,
} from 'three';
import type { FileTypeId, Nullish } from '@witmetrics/utils';
import { FILE_TYPE_ID } from '@witmetrics/utils/constants';
import { isPLY, isScan, isSTL } from '@witmetrics/utils/files';
import { degToRad, isNumber } from '@witmetrics/utils/numbers';
import { GINGIVA } from '@witmetrics/unison-core';
import { createPreviewImage, PREVIEW_SIZE } from '@/utils/images';
import { TrackballControls } from '@/utils/scans/trackballControls';
import {
  getDirectionalLights,
  getObjectsZoom,
} from '@/providers/SceneProvider/utils';
import { STLLoader } from './stlLoader';
import { PLYLoader } from './plyLoader';
import { buildDeltafaceConfig } from './deltafaceLoader';
import { parseDeltafaceZipFile } from './deltafaceLoader/utils';

const { RETAINER, SPLINT } = FILE_TYPE_ID;

export const CAMERA_OFFSET_Z = 50;

export function loadScan(
  name: string,
  data: ArrayBuffer,
  fileTypeId?: FileTypeId | Nullish
): Mesh | null {
  if (!isScan(name)) return null;
  let mesh: Mesh | null = null;
  if (isSTL(name)) {
    mesh = loadSTL(data, fileTypeId);
  } else if (isPLY(name)) {
    mesh = loadPLY(data);
  }
  if (!mesh) return null;
  mesh.position.set(0, 0, 0);
  mesh.scale.set(0.5, 0.5, 0.5);
  mesh.rotation.set((3 * Math.PI) / 2, 0, (3 * Math.PI) / 2);
  return mesh;
}

export function loadSTL(data: any, fileTypeId?: FileTypeId | Nullish): Mesh {
  const loader = new STLLoader();
  const stl = loader.parse(data);
  return buildSTLModel(stl, fileTypeId);
}

export function loadPLY(data: any): Mesh {
  const loader = new PLYLoader();
  const ply = loader.parse(data);
  return buildPLYModel(ply);
}

export function buildSTLModel(
  geometry: BufferGeometry,
  fileTypeId?: FileTypeId | Nullish
): Mesh {
  // isAppliance = Retainers & Splints
  const isAppliance = !!fileTypeId && [RETAINER, SPLINT].includes(fileTypeId);
  const material = isAppliance
    ? new MeshPhysicalMaterial({
        side: DoubleSide,

        attenuationDistance: 0.4,
        attenuationColor: 0xffffff,

        normalScale: new Vector2(0.1, 0.1),

        transmission: 0.95, // 1.0 is fully transmissive (like glass)
        thickness: 0.2,

        color: 0xf7fafb,
        emissive: 0x000000,

        roughness: 0, // Keep it low for a shiny, clean look

        clearcoat: 0.2,
        sheen: 0.4,

        ior: 2, // Index of Refraction (1.5 is standard for plastic)

        transparent: false, // Keep this FALSE when using high transmission
      })
    : new MeshPhongMaterial({
        side: DoubleSide,
        combine: MultiplyOperation,
        transparent: isAppliance,
        opacity: isAppliance ? 0.5 : 1,
        color: isAppliance ? 0x1fb9f4 : 0xffe0c4,
        emissive: 0x2d2d2d,
        specular: 0x303030,
        shininess: isAppliance ? 90 : 70,
        reflectivity: 1,
        refractionRatio: 1,
      });
  const mesh = new Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function buildPLYModel(geometry: BufferGeometry): Mesh {
  const material = new MeshBasicMaterial({
    color: 0xffffff,
    side: DoubleSide,
    vertexColors: true,
    transparent: false,
  });
  const mesh = new Mesh(geometry, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

export function centerObjects(objects: Object3D[]): Group {
  let group = new Group();
  group.add(...objects);
  const box = new Box3().setFromObject(group);
  group.position.set(
    -(box.min.x + box.max.x) / 2,
    -(box.min.y + box.max.y) / 2,
    -(box.min.z + box.max.z) / 2
  );
  return group;
}

async function renderObjectsToPreview(
  objects: Object3D[],
  options: { size?: number } = {}
): Promise<Blob> {
  const size = isNumber(options.size) ? options.size : PREVIEW_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const scene = new ThreeScene();
  const camera = new PerspectiveCamera(10, 1, 0.1, 1000);
  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setSize(size, size);
  // Match SceneProvider's default background so transparent appliance materials
  // attenuate against the same grey they do in the live viewer.
  renderer.setClearColor(0xf3f5f7, 1);
  camera.position.z = CAMERA_OFFSET_Z;
  const controls = new TrackballControls(camera, renderer.domElement);
  scene.add(new AmbientLight(0xffffff, 1));
  controls.object.add(...getDirectionalLights());
  scene.add(controls.object);
  controls.update();
  const group = centerObjects(objects);
  group.name = 'file_0';
  scene.add(group);
  // TODO: Shouldn't need to copy/paste from SceneProvider
  const { distance, center, direction } = getObjectsZoom([group], controls);
  controls.maxDistance = distance * 10;
  controls.target.copy(center);
  controls.object.near = distance / 100;
  controls.object.far = distance * 100;
  controls.object.updateProjectionMatrix();
  controls.object.position.copy(controls.target).sub(direction);
  controls.update();
  controls.setDefaults();
  renderer.render(scene, camera);
  return createPreviewImage(canvas.toDataURL('image/png'));
}

export async function createScanPreview(
  file: File,
  options: { size?: number; fileTypeId?: FileTypeId | Nullish } = {}
) {
  const arrayBuffer = await file.arrayBuffer();
  const scan = await loadScan(file.name, arrayBuffer, options.fileTypeId);
  if (!scan) throw new Error('Unable to build scan');
  return renderObjectsToPreview([scan], options);
}

export async function createSweethSequencePreview(
  file: File | Blob,
  options: { size?: number } = {}
) {
  const buffers = await parseDeltafaceZipFile(file);
  const config = buildDeltafaceConfig(buffers);
  const meshGroup = new Group();
  config.teeth.forEach((tooth, key) => {
    const startFrame = config.frames.get(key)?.[0];
    if (startFrame) {
      tooth.matrix.copy(startFrame);
      tooth.matrixAutoUpdate = false;
    }
    meshGroup.add(tooth);
  });
  const mandible = config.gingivas.get(GINGIVA.MANDIBLE);
  const maxilla = config.gingivas.get(GINGIVA.MAXILLA);
  if (mandible) meshGroup.add(mandible);
  if (maxilla) meshGroup.add(maxilla);
  // Mirrors DeltafaceStepViewer's buildMeshGroup orientation
  meshGroup.rotateZ(degToRad(-90));
  meshGroup.rotateY(degToRad(-90));
  return renderObjectsToPreview([meshGroup], options);
}
